import { alpha } from '@material-ui/core';
import styledConsts, {
  ThemeColorName,
} from 'app/styles/styledConsts';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import { Opt, PropsOf } from 'app/tools/general.types';
import { hashCode } from 'app/tools/utils';
import _ from 'lodash';
import React from 'react';
import styled, { css } from 'styled-components/macro';
import { Merge } from 'type-fest';
import {
  AppsIconSvg,
  EyeIconSvg,
  PictureIconSvg,
  TvIconSvg,
} from '../icon/icon-lib';
import AppImage from './AppImage';

function seededRandom(seed: number) {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
}

type ContainerOpts = {
  $color: ThemeColorName;
  $height?: string;
};
const FallbackBox = styled.div<ContainerOpts>`
  ${styledMixins.flexCenter}
  flex: 1 1 auto;
  ${p =>
    p.$height &&
    css`
      height: ${p.$height};
      width: ${p.$height};
    `}
  padding: ${styledFuncs.pad(0.2)};
  > svg {
    ${p =>
      p.$height
        ? css`
            height: calc(${p.$height}*0.8);
            width: calc(${p.$height}*0.8);
          `
        : css`
            margin: 10%;
          `}
    fill: ${p => alpha(styledConsts[p.$color](p), 0.2)};
  }
`;

const shouldError = (
  e: React.SyntheticEvent<HTMLImageElement, Event>
): boolean => {
  const imgRef = e.currentTarget;
  if (!imgRef) return true;
  const dims = _.pick(imgRef, 'naturalWidth', 'naturalHeight');
  return !dims || dims.naturalWidth <= 1 || dims.naturalHeight <= 1;
};

export const ProductPictureFallback: React.VFC<{
  height?: string;
  id?: number | string;
  className?: string;
}> = ({ height, id: idIn = 1, className }) => {
  const id = typeof idIn === 'number' ? idIn : hashCode(idIn);
  const seeded1 = Math.floor(seededRandom(id * 1000));
  const seeded2 = Math.floor(seededRandom(id + 1) * 1000);

  const Icon = (
    [AppsIconSvg, PictureIconSvg, EyeIconSvg, TvIconSvg] as const
  )[seeded1 % 4];

  const color = (['cPrimary', 'cSecondary', 'cError'] as const)[
    seeded2 % 3
  ];

  return (
    <FallbackBox $color={color} $height={height} {...{ className }}>
      <Icon />
    </FallbackBox>
  );
};

type Props = Merge<
  PropsOf<typeof AppImage>,
  {
    productId: Opt<number | string>;
    pendingSrc?: boolean;
    height?: string;
    sideSizeForCornerRadius?: string;
    alt?: string;
  }
>;
export default function ProductPictureOrDefault({
  productId: id = 1,
  alt = 'product',
  height,
  sideSizeForCornerRadius,
  ...props
}: Props): JSX.Element {
  const sideSize = sideSizeForCornerRadius || height || '150px';
  return (
    <AppImage
      onLoadShouldError={shouldError}
      fallbackImgNode={<ProductPictureFallback {...{ id, height }} />}
      css={css`
        > img {
          border-radius: min(calc(${sideSize} / 15), 10px);
        }
      `}
      {...{ alt, height }}
      {...props}
    />
  );
}
